import React, { FC, useEffect, useState } from 'react';
import { Checkbox, Modal, Tooltip, Button, Form, Radio, message, Spin } from 'antd';

import close from '../../../../assets/RoundedClose.svg';
import done from '../../../../assets/Done, Check.svg';
import ServiceComponentsIcon from '../../../../assets/ServiceComponentsIcon.png';
import InfoIcon from '../../../../components/Icons/infoIcon';
import {
    useLazyGetKickOffPreviewFormQuery,
    usePreviewFormDiscardChangesMutation,
    useSaveKickOffPreviewFormMutation
} from '../../../../api/expenseAreas';
import { createAbortController, uploadFileKickOffPreview } from '../../../../api/axiosCruds';
import { Loader } from '../../../../components/Loader/Loader';
import { yupSync } from '../../../../utils';
import * as Yup from 'yup';
import { CustomMultiSelect } from '../../../../components/Fields/CustomMultiSelect';
import { FloatSelect } from '../../../../components/Fields/FloatSelect';
import { useGetSchoolTypesQuery } from '../../../../api/common';

import { FloatInput } from '../../../../components/Fields/FloatInput';
import { FloatTextArea } from '../../../../components/Fields/FloatTextArea';
import i from '../../../../assets/i.svg';
import { FilePicker } from '../../../../components/Fields/FilePicker';
import ArrowDown from '../../../../components/Icons/ArrowDown';
import Edit from '../../../../components/Icons/Edit';
import { PREVIEW_TOASTS } from './previewToastMessages';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    expenseAreaId: number;
}

export interface SaveData {
    id: number;
    slug: string;
    title: string;
    description: string;
    fields: FieldI[];
}

export interface FieldI {
    id: number;
    slug: string;
    type: string;
    label: string;
    description: string;
    options: any[];
    settings: { type: string; switchValue: boolean }[];
    order: number;
    answer: string | number[] | null | number;
    component_id?: number | null;
}

const fieldInstanceKey = (id: number, componentId?: number | null) =>
    `${id}__${componentId == null ? 'null' : componentId}`;

const isSameFieldInstance = (
    field: { id: number; component_id?: number | null },
    id: number,
    componentId?: number | null
) => field.id === id && (field.component_id ?? null) === (componentId ?? null);

const isKickOffFieldRequired = (field: { settings?: { type: string; switchValue: boolean }[] }) =>
    !!field?.settings?.find((s) => s.type === 'required')?.switchValue;

const validationSchema = Yup.object().shape({
    contact_name: Yup.string().required('This is a required field'),
    first_name: Yup.string().required('This is a required field'),
    name: Yup.string().required('This is a required field'),
    trading_name: Yup.string(),
    website: Yup.string()
        .required('This is a required field')
        .matches(/^(https?:\/\/)/, 'Oops! Your link is not correct'),
    last_name: Yup.string().required('This is a required field'),
    email: Yup.string().email('Enter your email: e.g. email@domain.com').required('This is a required field'),
    secondary_email: Yup.string().email('Enter your email: e.g. email@domain.com'),
    school: Yup.object().required('This is a required field'),
    b_bbee: Yup.object().required('This is a required field'),
    annual_revenue: Yup.object().required('This is a required field'),
    employees_number: Yup.object().required('This is a required field'),
    sub_regions: Yup.object().required('This is a required field'),
    job: Yup.string().required('This is a required field'),
    phone_number: Yup.string()
        .required('This is a required field')
        .matches(/^[^_]*$/, 'This is a required field'),
    registration_num: Yup.string()
        .required('This is a required field')
        .matches(/^[^_]*$/, 'This is a required field'),
    vat_number: Yup.string()
        .required('This is a required field')
        .matches(/^[^_]*$/, 'This is a required field'),

    password: Yup.string()
        .required('This is a required field')
        .test('password-requirements', '', (value) => {
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
            return passwordRegex.test(value);
        }),
    confirmPassword: Yup.string().required('This is a required field')
});

export const AdminKickOffPreviewModal: FC<ModalProps> = ({ openModal, setOpenModal, expenseAreaId }) => {
    const [getPreview, { data: previewResponse, isFetching, isError, error }] = useLazyGetKickOffPreviewFormQuery();
    const formData = previewResponse?.data ?? previewResponse;
    const { steps, form_data, service_components, progress_data } = formData ?? {};

    const [saveData, setSaveData] = useState<any>();
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [errorFiles, setErrorFiles] = useState<{ key: string; error: string }[]>([]);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [loadingType, setLoadingType] = useState('');

    const types = {};
    const { data: dataSchoolTypes } = useGetSchoolTypesQuery(types);
    const [savePreview, { isLoading: isLoadingSave, isError: isSaveError, error: saveError, isSuccess }] =
        useSaveKickOffPreviewFormMutation();
    const [discardPreviewChanges, { isLoading: isDiscardingPreview }] = usePreviewFormDiscardChangesMutation();

    const isServiceComponents = saveData?.fields?.[0]?.slug === 'service_components';

    const [subRegionsOptions, setSubregionsOptions] = useState<any>([]);
    const [filtredSubRegion, setFiltredSubRegion] = useState<any>([]);
    const [filteredAnnual, setFilteredAnnual] = useState([]);
    const [filteredEmpl, setFilteredEmpl] = useState([]);
    const [filteredProvincial, setFilteredProvincial] = useState([]);
    const [filteredBBE, setFilteredBBE] = useState<string[]>([]);

    const editedField = progress_data?.edited_fields ?? [];

    const handleDiscardAndExit = () => {
        if (isDiscardingPreview) return;

        discardPreviewChanges({
            expense_area_id: expenseAreaId,
            type: 'kick-off-questionnaire'
        })
            .unwrap()
            .then(() => {
                setOpenModal(false);
            })
            .catch((err: { data?: { message?: string } }) => {
                if (err?.data?.message) {
                    message.error(err.data.message);
                }
            });
    };

    const getFormData = (step: null | number) => {
        getPreview({ expense_area_id: expenseAreaId, step });
    };

    useEffect(() => {
        if (openModal && expenseAreaId) {
            getPreview({ expense_area_id: expenseAreaId, step: null });
        }
    }, [openModal, expenseAreaId]);

    const [form] = Form.useForm();

    const SelectedProvince: any = saveData?.fields?.find((elem: any) => elem.slug === 'provincial_coverage')?.answer;
    const SelectedAnnual: any = saveData?.fields?.find((elem: any) => elem.slug === 'annual_revenue')?.answer;
    const SelectedEmpl: any = saveData?.fields?.find((elem: any) => elem.slug === 'number_employees')?.answer;
    const SelectedBBE: any = saveData?.fields?.find((elem: any) => elem.slug === 'b_bbee')?.answer;
    const SelectedSubRegions: any = saveData?.fields?.find((elem: any) => elem.slug === 'sub_region')?.answer;

    useEffect(() => {
        if (isSuccess && steps?.length) {
            const activeIndex = steps.findIndex((step: any) => step.is_active);
            const isLastStep = activeIndex === steps.length - 1;
            if (isLastStep && loadingType === 'next') {
                message.success(PREVIEW_TOASTS.kickOffChangesSaved);
                setOpenModal(false);
            }
        }
    }, [isSuccess, steps, loadingType, setOpenModal]);

    useEffect(() => {
        if (isError && error) {
            const apiMessage = (error as { data?: { message?: string } })?.data?.message;
            if (apiMessage) {
                message.error(apiMessage);
            }
        }
    }, [isError, error]);

    useEffect(() => {
        if (isSaveError && saveError) {
            const apiMessage = (saveError as { data?: { message?: string } })?.data?.message;
            if (apiMessage) {
                message.error(apiMessage);
            }
        }
    }, [isSaveError, saveError]);

    useEffect(() => {
        if (SelectedAnnual) {
            if (dataSchoolTypes?.data?.sc_annual_revenue) {
                const annual = [...dataSchoolTypes?.data?.sc_annual_revenue];
                const filteredArray: any = annual.filter((item) => !SelectedAnnual.includes(item.id));
                setFilteredAnnual(filteredArray);
            }
        } else {
            setFilteredAnnual(dataSchoolTypes?.data?.sc_annual_revenue);
        }
    }, [dataSchoolTypes?.data?.sc_annual_revenue, SelectedAnnual]);

    useEffect(() => {
        if (SelectedProvince) {
            if (dataSchoolTypes?.data?.provinces) {
                const empl = [...dataSchoolTypes?.data?.provinces];
                const filteredArray: any = empl.filter((item) => !SelectedProvince.includes(item.id));
                setFilteredProvincial(filteredArray);
            }
        } else {
            setFilteredProvincial(dataSchoolTypes?.data?.provinces);
        }
    }, [dataSchoolTypes?.data?.provinces, SelectedProvince]);

    useEffect(() => {
        if (!dataSchoolTypes?.data?.provinces) return;

        const provinces = [...dataSchoolTypes?.data?.provinces];

        let subRegionsArray: any[] = [];

        provinces.forEach((elem) => {
            if (SelectedProvince?.includes(elem.id)) {
                subRegionsArray.push(...elem.sub_regions);
            }
        });
        const filteredArraySubRegions: any = subRegionsArray.filter((item) => !SelectedSubRegions?.includes(item.id));

        setFiltredSubRegion(filteredArraySubRegions);
        setSubregionsOptions(subRegionsArray);
    }, [SelectedProvince, SelectedSubRegions]);

    useEffect(() => {
        if (SelectedEmpl) {
            if (dataSchoolTypes?.data?.sc_employees_number) {
                const empl = [...dataSchoolTypes?.data?.sc_employees_number];
                const filteredArray: any = empl.filter((item) => !SelectedEmpl.includes(item.id));
                setFilteredEmpl(filteredArray);
            }
        } else {
            setFilteredEmpl(dataSchoolTypes?.data?.sc_employees_number);
        }
    }, [dataSchoolTypes?.data?.sc_employees_number, SelectedEmpl]);

    useEffect(() => {
        if (filteredBBE) {
            if (dataSchoolTypes?.data?.sc_b_bbee) {
                const empl = [...dataSchoolTypes?.data?.sc_b_bbee];
                const filteredArray: any = empl.filter((item) => !SelectedBBE?.includes(item.id));
                setFilteredBBE(filteredArray);
            }
        } else {
            setFilteredBBE(dataSchoolTypes?.data?.sc_b_bbee);
        }
    }, [dataSchoolTypes?.data?.sc_b_bbee, SelectedBBE]);

    const handleUpdateAnswer = (fieldId: number, value: string | number, componentId?: number | null) => {
        setSaveData((prev: any) => {
            if (!prev) return prev;

            return {
                ...prev,
                fields: prev.fields.map((field: any) => {
                    if (!isSameFieldInstance(field, fieldId, componentId)) return field;

                    if (field.type === 'multiple-choice') {
                        const current = Array.isArray(field.answer) ? field.answer : [];
                        const exists = current.includes(value);

                        return {
                            ...field,
                            answer: exists ? current.filter((v: any) => v !== value) : [...current, value]
                        };
                    }

                    return {
                        ...field,
                        answer: String(value)
                    };
                })
            };
        });
    };

    const handleUpdateSuppInfo = (
        fieldId: number,
        value: any,
        type?: string,
        slug?: string,
        options?: any[],
        componentId?: number | null
    ) => {
        const errors = [...errorFields];
        const newErrors = errors.filter((item) => item !== fieldInstanceKey(fieldId, componentId));

        setErrorFields(newErrors);

        setSaveData((prev: any) => {
            if (!prev) return prev;

            return {
                ...prev,
                fields: prev.fields.map((field: any) => {
                    if (isSameFieldInstance(field, fieldId, componentId)) {
                        if (type === 'multiple') {
                            const current = Array.isArray(field.answer) ? field.answer : [];

                            if (value === 'selectedAll') {
                                const allValues = options?.map((o: any) => o.id) || [];
                                return {
                                    ...field,
                                    answer: allValues
                                };
                            }

                            const exists = current.includes(value);
                            return {
                                ...field,
                                answer: exists ? current.filter((v: any) => v !== value) : [...current, value]
                            };
                        } else if (type === 'single') {
                            return {
                                ...field,
                                answer: value === true ? 1 : value === false ? 0 : null
                            };
                        }

                        return {
                            ...field,
                            answer: [value]
                        };
                    }

                    if (slug === 'province' && field.slug === 'sub_region') {
                        return {
                            ...field,
                            answer: null
                        };
                    }

                    return field;
                })
            };
        });
    };

    const validateRequiredFields = (data: SaveData | null) => {
        if (!data) {
            return { isValid: false, invalidFields: [] };
        }

        const invalidFields: string[] = [];

        for (const field of data.fields) {
            const requiredSetting = field.settings.find((s) => s.type === 'required');

            if (!requiredSetting || !requiredSetting.switchValue) continue;

            const answer = field.answer;

            let isFieldValid = true;

            if (data?.slug === 'supplier_information') {
                if (field.slug === 'vat_registered') {
                    isFieldValid = answer === 1 || answer === 0;
                } else {
                    isFieldValid = Array.isArray(answer) && answer.length > 0;
                }

                if (!isFieldValid) {
                    invalidFields.push(fieldInstanceKey(field.id, field.component_id));
                }
            } else if (data.slug === 'evaluation_criteria') {
                if (field.type === 'slider') {
                    isFieldValid = !!answer;
                }
                if (!isFieldValid) {
                    invalidFields.push(fieldInstanceKey(field.id, field.component_id));
                }
            } else {
                if (field.type === 'multiple-choice' || field.type === 'dropdown') {
                    isFieldValid = Array.isArray(answer) && answer.length > 0;
                } else if (field.type === 'file-upload') {
                    isFieldValid = !!answer;
                } else {
                    isFieldValid = typeof answer === 'string' && answer.trim().length > 0;
                }

                if (!isFieldValid) {
                    invalidFields.push(fieldInstanceKey(field.id, field.component_id));
                }
            }
        }

        return {
            isValid: invalidFields.length === 0,
            invalidFields
        };
    };

    const saveForm = (type?: string | null) => {
        const { isValid, invalidFields } = validateRequiredFields(saveData);

        setErrorFields(invalidFields);

        const items = [...(steps ?? [])];
        const activeStep = items.find((item) => item.is_active)?.key;

        if (type === 'save_and_exit') {
            setLoadingType('save_and_exit');
            savePreview({
                expense_area_id: expenseAreaId,
                step: activeStep,
                formData: saveData
            })
                .unwrap()
                .then(() => {
                    message.success(PREVIEW_TOASTS.kickOffChangesSaved);
                    setOpenModal(false);
                })
                .finally(() => setLoadingType(''));
            return;
        }

        if (type === 'next') {
            setLoadingType('next');
            if (!isValid) {
                setLoadingType('');
                return;
            }

            savePreview({
                expense_area_id: expenseAreaId,
                step: activeStep,
                action: 'next_step',
                formData: saveData
            })
                .unwrap()
                .then(() => {
                    getFormData(null);
                })
                .finally(() => setLoadingType(''));
        }
    };

    useEffect(() => {
        if (form_data) {
            setSaveData({ ...form_data });
        }
    }, [form_data]);

    const updateAnswer = (id: number, value: any, componentId?: number | null) => {
        const errors = [...errorFields];
        const newErrors = errors.filter((item) => item !== fieldInstanceKey(id, componentId));

        setErrorFields(newErrors);

        setSaveData((prev: any) => {
            if (!prev) return prev;

            return {
                ...prev,
                fields: prev.fields.map((field: any) => {
                    if (isSameFieldInstance(field, id, componentId)) {
                        if (field.type === 'dropdown') {
                            return {
                                ...field,
                                answer: [value]
                            };
                        } else if (field.type === 'multiple-choice') {
                            const current = Array.isArray(field.answer) ? field.answer : [];
                            const exists = current.includes(value);

                            return {
                                ...field,
                                answer: exists ? current.filter((v: any) => v !== value) : [...current, value]
                            };
                        } else {
                            return {
                                ...field,
                                answer: value
                            };
                        }
                    }

                    return field;
                })
            };
        });
    };

    const deleteFile = (field_id: number, componentId?: number | null) => {
        setSaveData((prev: any) => {
            if (!prev) return prev;

            return {
                ...prev,
                fields: prev.fields.map((field: any) => {
                    if (!isSameFieldInstance(field, field_id, componentId)) return field;

                    return {
                        ...field,
                        answer: null
                    };
                })
            };
        });
    };

    const uploadFileHandler = async (
        fileList: any[],
        section_id: number,
        field_id: number,
        component_id?: number | null
    ) => {
        const fileFieldKey = fieldInstanceKey(field_id, component_id);
        const errors = [...errorFields];
        const newErrors = errors.filter((item) => item !== fileFieldKey);

        setErrorFields(newErrors);
        setErrorFiles((prev) => [...prev, { key: fileFieldKey, error: '' }]);

        try {
            if (abortController) {
                abortController.abort();
            }

            if (!fileList[0]) return;

            const contentFile: any = fileList?.[0]?.originFileObj;
            const fileData = new FormData();
            const fileObj: File = contentFile;
            const fileName: string = fileObj?.name;

            fileData.append('file', fileObj, fileName);
            fileData.append('section_id', section_id.toString());
            fileData.append('field_id', field_id.toString());
            fileData.append('component_id', component_id ? component_id.toString() : '');

            const items = [...(steps ?? [])];
            const activeStep = items.find((item) => item.is_active)?.key;

            const newAbortController = createAbortController();
            setAbortController(newAbortController);

            const response = await uploadFileKickOffPreview(
                {
                    expense_area_id: expenseAreaId,
                    step: activeStep,
                    data: fileData
                },
                newAbortController.signal
            );

            setSaveData((prev: any) => {
                if (!prev) return prev;

                return {
                    ...prev,
                    fields: prev.fields.map((field: any) => {
                        if (!isSameFieldInstance(field, field_id, component_id)) return field;

                        return {
                            ...field,
                            answer: response.data.data
                        };
                    })
                };
            });
        } catch (uploadError: any) {
            setErrorFiles((prev) => [
                ...prev,
                {
                    key: fileFieldKey,
                    error: uploadError?.response?.data?.message
                }
            ]);
        }
    };

    return (
        <>
            <Modal
                className="questionary_modal"
                destroyOnClose
                centered
                open={openModal}
                onOk={() => {}}
                onCancel={() => {
                    setOpenModal(false);
                }}
                closeIcon={<img src={close} />}
                footer={<></>}
            >
                <div className="modal questionary_modal_wrapper">
                    <div className="modal__header meeting" style={{ height: '88px' }}>
                        <div className="modal__title">Kick-off Questionnaire Preview</div>
                    </div>

                    {isFetching && !form_data ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                            <Spin />
                        </div>
                    ) : (
                        <>
                    <div className="questionary_modal__main">
                        <div className="questionary_modal__steps">
                            {steps?.map((elem: any, index: number) => {
                                const isActive = elem?.is_active;
                                const isDone = isActive ? false : elem.is_completed;
                                return (
                                    <>
                                        <div
                                            className={`questionary_modal__section 
                                                                            ${
                                                                                isActive
                                                                                    ? 'active'
                                                                                    : isDone
                                                                                    ? 'done'
                                                                                    : 'pending'
                                                                            }
                                                                        `}
                                        >
                                            {isDone ? (
                                                <div className="number_of_section">
                                                    <img src={done} />
                                                </div>
                                            ) : (
                                                <div className="number_of_section">{index + 1}</div>
                                            )}
                                            {elem?.title}
                                        </div>
                                        {index !== steps?.length - 1 && (
                                            <div className="questionary_modal__divider"></div>
                                        )}
                                    </>
                                );
                            })}
                        </div>
                        <div className="questionary_modal__form">
                            <div className="questionary_modal__form-header">
                                <div className="questionary_modal__form-header__row">
                                    <span className="questionary_modal__form-header__row-title">
                                        <img src={ServiceComponentsIcon} /> {form_data?.title}{' '}
                                    </span>
                                    <div className="number-of-section">
                                        Section {steps?.find((elem: any) => elem.is_active)?.key} of {steps?.length}
                                    </div>
                                </div>
                                <div className="questionary_modal__form-header__row description">
                                    {form_data?.description}
                                </div>
                            </div>

                            {saveData?.fields?.map((elem: any) => {
                                const elemFieldKey = fieldInstanceKey(elem.id, elem.component_id);
                                const isRequired = isKickOffFieldRequired(elem);

                                const isMatch = editedField.some(
                                    (item: any) =>
                                        item.id === elem.id &&
                                        (item.component_id == null || item.component_id === elem.component_id)
                                );

                                if (elem?.slug === 'service_components') {
                                    return (
                                        <div className="questionary_modal__formItem formItem">
                                            <div className="formItem__label">
                                                {elem?.label}
                                                {isRequired && <span className="red">*</span>}
                                            </div>
                                            <div className="formItem__description">{elem.description}</div>
                                            <div>
                                                {service_components?.map((comp: any) => {
                                                    return (
                                                        <div className="questionary_modal__item">
                                                            <Checkbox
                                                                onChange={() =>
                                                                    handleUpdateAnswer(
                                                                        elem?.id,
                                                                        comp?.id,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                checked={elem?.answer?.includes(comp?.id)}
                                                                disabled={false}
                                                            />{' '}
                                                            {comp?.name}{' '}
                                                            <Tooltip title={comp?.description}>
                                                                <div>
                                                                    <InfoIcon fill="#808CB2" />
                                                                </div>
                                                            </Tooltip>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                } else if (saveData.slug === 'supplier_information') {
                                    return (
                                        <Form form={form} className="questionary_modal__second-form second-form">
                                            <>
                                                {/* provincial_coverage */}
                                                {elem.slug === 'provincial_coverage' && (
                                                    <div className="second-form__block">
                                                        <Form.Item
                                                            className="input-wrapper-multiselect"
                                                            rules={yupSync('provinces', validationSchema, true)}
                                                        >
                                                            <CustomMultiSelect
                                                                disabled={false}
                                                                required={isRequired}
                                                                floatLabel={elem.label}
                                                                err={false}
                                                                className="input-select"
                                                                options={filteredProvincial}
                                                                value={
                                                                    elem.answer?.length > 0
                                                                        ? dataSchoolTypes?.data?.provinces
                                                                              ?.filter((item: any) =>
                                                                                  elem.answer.includes(item.id)
                                                                              )
                                                                              .map((item: any) => ({
                                                                                  id: item.id,
                                                                                  name: item.name
                                                                              }))
                                                                        : []
                                                                }
                                                                onChange={(value: any) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value?.id,
                                                                        'multiple',
                                                                        '',
                                                                        dataSchoolTypes?.data?.provinces,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                hasError={() => errorFields.includes(elemFieldKey)}
                                                                setTouched={() => {}}
                                                                prefix={undefined}
                                                                deleteItem={(value) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value,
                                                                        'multiple',
                                                                        undefined,
                                                                        undefined,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                deleteAllItems={() => {
                                                                    setSaveData((prev: any) => {
                                                                        if (!prev) return prev;

                                                                        return {
                                                                            ...prev,
                                                                            fields: prev.fields.map((field: any) => {
                                                                                if (
                                                                                    isSameFieldInstance(
                                                                                        field,
                                                                                        elem.id,
                                                                                        elem.component_id
                                                                                    ) ||
                                                                                    field.slug === 'sub_region'
                                                                                ) {
                                                                                    return {
                                                                                        ...field,
                                                                                        answer: null
                                                                                    };
                                                                                }

                                                                                return field;
                                                                            })
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                        </Form.Item>
                                                        {errorFields.includes(elemFieldKey) && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 10px 0' }}
                                                            >
                                                                This is a required field
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* sub_region */}
                                                {elem.slug === 'sub_region' && (
                                                    <div className="second-form__block">
                                                        <Form.Item
                                                            className="input-wrapper"
                                                            rules={yupSync('sub_regions', validationSchema, true)}
                                                        >
                                                            <CustomMultiSelect
                                                                disabled={
                                                                    !SelectedProvince ||
                                                                    false
                                                                }
                                                                required={isRequired}
                                                                floatLabel={elem.label}
                                                                err={false}
                                                                className="input-select"
                                                                options={filtredSubRegion}
                                                                value={
                                                                    elem.answer?.length > 0
                                                                        ? subRegionsOptions
                                                                              ?.filter((item: any) =>
                                                                                  elem.answer.includes(item.id)
                                                                              )
                                                                              .map((item: any) => ({
                                                                                  id: item.id,
                                                                                  name: item.name
                                                                              }))
                                                                        : []
                                                                }
                                                                onChange={(value: any) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value?.id,
                                                                        'multiple',
                                                                        '',
                                                                        subRegionsOptions,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                hasError={() => errorFields.includes(elemFieldKey)}
                                                                setTouched={() => {}}
                                                                prefix={undefined}
                                                                deleteItem={(value) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value,
                                                                        'multiple',
                                                                        undefined,
                                                                        undefined,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                deleteAllItems={() => {
                                                                    setSaveData((prev: any) => {
                                                                        if (!prev) return prev;

                                                                        return {
                                                                            ...prev,
                                                                            fields: prev.fields.map((field: any) => {
                                                                                if (
                                                                                    isSameFieldInstance(
                                                                                        field,
                                                                                        elem.id,
                                                                                        elem.component_id
                                                                                    )
                                                                                ) {
                                                                                    return {
                                                                                        ...field,
                                                                                        answer: null
                                                                                    };
                                                                                }

                                                                                return field;
                                                                            })
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                        </Form.Item>

                                                        {errorFields.includes(elemFieldKey) && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 10px 0' }}
                                                            >
                                                                This is a required field
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* annual_revenue */}
                                                {elem.slug === 'annual_revenue' && (
                                                    <div className="second-form__block">
                                                        <Form.Item
                                                            className="input-wrapper"
                                                            rules={yupSync('annual_revenue', validationSchema, true)}
                                                        >
                                                            <CustomMultiSelect
                                                                disabled={false}
                                                                required={isRequired}
                                                                floatLabel={elem.label}
                                                                err={false}
                                                                className="input-select"
                                                                options={filteredAnnual}
                                                                value={
                                                                    elem.answer?.length > 0
                                                                        ? dataSchoolTypes?.data?.sc_annual_revenue
                                                                              ?.filter((item: any) =>
                                                                                  elem.answer.includes(item.id)
                                                                              )
                                                                              .map((item: any) => ({
                                                                                  id: item.id,
                                                                                  name: item.name
                                                                              }))
                                                                        : []
                                                                }
                                                                onChange={(value: any) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value?.id,
                                                                        'multiple',
                                                                        '',
                                                                        dataSchoolTypes?.data?.sc_annual_revenue,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                hasError={() => errorFields.includes(elemFieldKey)}
                                                                setTouched={() => {}}
                                                                prefix={undefined}
                                                                deleteItem={(value) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value,
                                                                        'multiple',
                                                                        undefined,
                                                                        undefined,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                deleteAllItems={() => {
                                                                    setSaveData((prev: any) => {
                                                                        if (!prev) return prev;

                                                                        return {
                                                                            ...prev,
                                                                            fields: prev.fields.map((field: any) => {
                                                                                if (
                                                                                    isSameFieldInstance(
                                                                                        field,
                                                                                        elem.id,
                                                                                        elem.component_id
                                                                                    )
                                                                                ) {
                                                                                    return {
                                                                                        ...field,
                                                                                        answer: null
                                                                                    };
                                                                                }

                                                                                return field;
                                                                            })
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                        </Form.Item>

                                                        {errorFields.includes(elemFieldKey) && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 10px 0' }}
                                                            >
                                                                This is a required field
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* number_employees */}
                                                {elem.slug === 'number_employees' && (
                                                    <div className="second-form__block">
                                                        <Form.Item
                                                            className="input-wrapper"
                                                            rules={yupSync('employees_number', validationSchema, true)}
                                                        >
                                                            <CustomMultiSelect
                                                                disabled={false}
                                                                required={isRequired}
                                                                floatLabel={elem.label}
                                                                err={false}
                                                                className="input-select"
                                                                options={filteredEmpl}
                                                                value={
                                                                    elem.answer?.length > 0
                                                                        ? dataSchoolTypes?.data?.sc_employees_number
                                                                              ?.filter((item: any) =>
                                                                                  elem.answer.includes(item.id)
                                                                              )
                                                                              .map((item: any) => ({
                                                                                  id: item.id,
                                                                                  name: item.name
                                                                              }))
                                                                        : []
                                                                }
                                                                onChange={(value: any) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value?.id,
                                                                        'multiple',
                                                                        '',
                                                                        dataSchoolTypes?.data?.sc_employees_number,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                hasError={() => errorFields.includes(elemFieldKey)}
                                                                setTouched={() => {}}
                                                                prefix={undefined}
                                                                deleteItem={(value) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value,
                                                                        'multiple',
                                                                        undefined,
                                                                        undefined,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                deleteAllItems={() => {
                                                                    setSaveData((prev: any) => {
                                                                        if (!prev) return prev;

                                                                        return {
                                                                            ...prev,
                                                                            fields: prev.fields.map((field: any) => {
                                                                                if (
                                                                                    isSameFieldInstance(
                                                                                        field,
                                                                                        elem.id,
                                                                                        elem.component_id
                                                                                    )
                                                                                ) {
                                                                                    return {
                                                                                        ...field,
                                                                                        answer: null
                                                                                    };
                                                                                }

                                                                                return field;
                                                                            })
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                        </Form.Item>

                                                        {errorFields.includes(elemFieldKey) && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 10px 0' }}
                                                            >
                                                                This is a required field
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* vat_registered */}
                                                {elem.slug === 'vat_registered' && (
                                                    <div
                                                        className="second-form__block"
                                                        style={{ margin: '10px 0 0 0' }}
                                                    >
                                                        <div className="second-form__title">
                                                            {elem.label}
                                                            {isRequired && <span className="red">*</span>}
                                                        </div>

                                                        <Form.Item className="small-input-wrapper">
                                                            <Radio.Group
                                                                disabled={false}
                                                                name="radiogroup"
                                                                className="second-form__radiogroup"
                                                                value={
                                                                    elem.answer === 1
                                                                        ? true
                                                                        : elem.answer === 0
                                                                        ? false
                                                                        : null
                                                                }
                                                                onChange={(e) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem.id,
                                                                        e.target.value,
                                                                        'single',
                                                                        undefined,
                                                                        undefined,
                                                                        elem.component_id
                                                                    )
                                                                }
                                                            >
                                                                <Radio value={true}>Yes</Radio>
                                                                <Radio value={false}>No</Radio>
                                                            </Radio.Group>

                                                            {errorFields.includes(elemFieldKey) && (
                                                                <div
                                                                    className="error-text"
                                                                    style={{ margin: '10px 0 10px 0' }}
                                                                >
                                                                    This is a required field
                                                                </div>
                                                            )}
                                                        </Form.Item>
                                                    </div>
                                                )}
                                                {/* b_bbee */}
                                                {elem.slug === 'b_bbee' && (
                                                    <div className="second-form__block">
                                                        <div className="second-form__title">
                                                            {elem.label}
                                                            {isRequired && <span className="red">*</span>}
                                                        </div>
                                                        <Form.Item
                                                            className="input-wrapper"
                                                            rules={yupSync('b_bbee', validationSchema, true)}
                                                        >
                                                            <CustomMultiSelect
                                                                disabled={false}
                                                                required={isRequired}
                                                                floatLabel={elem.label}
                                                                err={false}
                                                                className="input-select"
                                                                options={filteredBBE}
                                                                value={
                                                                    elem.answer?.length > 0
                                                                        ? dataSchoolTypes?.data?.sc_b_bbee
                                                                              ?.filter((item: any) =>
                                                                                  elem.answer.includes(item.id)
                                                                              )
                                                                              .map((item: any) => ({
                                                                                  id: item.id,
                                                                                  name: item.name
                                                                              }))
                                                                        : []
                                                                }
                                                                onChange={(value: any) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value?.id,
                                                                        'multiple',
                                                                        '',
                                                                        dataSchoolTypes?.data?.sc_b_bbee,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                hasError={() => errorFields.includes(elemFieldKey)}
                                                                setTouched={() => {}}
                                                                prefix={undefined}
                                                                deleteItem={(value) =>
                                                                    handleUpdateSuppInfo(
                                                                        elem?.id,
                                                                        value,
                                                                        'multiple',
                                                                        undefined,
                                                                        undefined,
                                                                        elem?.component_id
                                                                    )
                                                                }
                                                                deleteAllItems={() => {
                                                                    setSaveData((prev: any) => {
                                                                        if (!prev) return prev;

                                                                        return {
                                                                            ...prev,
                                                                            fields: prev.fields.map((field: any) => {
                                                                                if (
                                                                                    isSameFieldInstance(
                                                                                        field,
                                                                                        elem.id,
                                                                                        elem.component_id
                                                                                    )
                                                                                ) {
                                                                                    return {
                                                                                        ...field,
                                                                                        answer: null
                                                                                    };
                                                                                }

                                                                                return field;
                                                                            })
                                                                        };
                                                                    });
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </div>
                                                )}
                                            </>
                                        </Form>
                                    );
                                } else {
                                    if (elem.type === 'short-answer') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                <div className="Field__input">
                                                    <div className="Field__row">
                                                        <FloatInput
                                                            disabled={
                                                                false
                                                            }
                                                            floatLabel={elem.label}
                                                            className="input"
                                                            required={isRequired}
                                                            onChange={(e: any) =>
                                                                updateAnswer(elem.id, e.target.value, elem.component_id)
                                                            }
                                                            value={elem.answer}
                                                            hasError={() => errorFields.includes(elemFieldKey)}
                                                            maxLength={60}
                                                            style={{ width: '600px' }}
                                                        />
                                                        <></>
                                                    </div>
                                                    {errorFields.includes(elemFieldKey) && (
                                                        <div className="error-text">This field is required</div>
                                                    )}
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'paragraph') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                <div className="Field__input">
                                                    <div className="Field__row">
                                                        <FloatTextArea
                                                            disabled={
                                                                false
                                                            }
                                                            floatLabel={elem.label}
                                                            className="input"
                                                            hasError={() => errorFields.includes(elemFieldKey)}
                                                            required={isRequired}
                                                            onChange={(e: any) =>
                                                                updateAnswer(elem.id, e.target.value, elem.component_id)
                                                            }
                                                            value={elem.answer}
                                                        />
                                                        <></>
                                                    </div>
                                                    {errorFields.includes(elemFieldKey) && (
                                                        <div className="error-text">This field is required</div>
                                                    )}
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'dropdown') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                <div className="Field__row">
                                                    <Form>
                                                        <Form.Item
                                                            className="input-wrapper dropdown-sign-supplier"
                                                            style={{ margin: '10px 0 0 0' }}
                                                        >
                                                            <FloatSelect
                                                                disabled={
                                                                    false
                                                                }
                                                                required={isRequired}
                                                                floatLabel={elem?.label}
                                                                err={errorFields.includes(elemFieldKey)}
                                                                setSearch={() => {}}
                                                                options={elem?.options?.map((elem: any) => ({
                                                                    name: elem.label,
                                                                    value: elem.label
                                                                }))}
                                                                onChange={(value: any) =>
                                                                    updateAnswer(
                                                                        elem.id,
                                                                        value?.name,
                                                                        elem.component_id
                                                                    )
                                                                }
                                                                search={''}
                                                                form={undefined}
                                                                hasError={() => !!errorFields.includes(elemFieldKey)}
                                                                type="common"
                                                                value={
                                                                    elem?.answer?.length > 0
                                                                        ? { name: elem?.answer, label: elem?.answer }
                                                                        : ''
                                                                }
                                                                style={{ maxWidth: '600px' }}
                                                            />
                                                        </Form.Item>
                                                        {errorFields.includes(elemFieldKey) && (
                                                            <div className="error-text" style={{ margin: '2px 0 0 0' }}>
                                                                This field is required
                                                            </div>
                                                        )}
                                                    </Form>
                                                    <></>
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'single-select') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                {elem.component_name && (
                                                    <div className="Field__component">{elem.component_name}</div>
                                                )}
                                                <div className="Field__title">
                                                    <div>
                                                        {elem.label}
                                                        {isRequired && <span className="red">*</span>}
                                                    </div>
                                                    <></>
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                <div className="Field__options">
                                                    {elem?.options?.map((option: any) => {
                                                        return (
                                                            <div className="Field__option">
                                                                <Radio
                                                                    disabled={
                                                                        false
                                                                    }
                                                                    onClick={() =>
                                                                        updateAnswer(
                                                                            elem.id,
                                                                            option?.value,
                                                                            elem.component_id
                                                                        )
                                                                    }
                                                                    checked={elem?.answer?.includes(option?.label)}
                                                                    className={elem?.error ? 'red' : ''}
                                                                />
                                                                {option?.label}
                                                            </div>
                                                        );
                                                    })}
                                                    {errorFields.includes(elemFieldKey) && (
                                                        <div className="error-text">This field is required</div>
                                                    )}
                                                </div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'multiple-choice') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                {elem.component_name && (
                                                    <div className="Field__component">{elem.component_name}</div>
                                                )}
                                                <div className="Field__title">
                                                    <div>
                                                        {elem.label}
                                                        {isRequired && <span className="red">*</span>}
                                                    </div>
                                                    <></>
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                <div className="Field__options">
                                                    {elem?.options?.map((option: any) => {
                                                        return (
                                                            <div
                                                                className="Field__option"
                                                                style={{ whiteSpace: 'pre-wrap' }}
                                                            >
                                                                <Checkbox
                                                                    disabled={
                                                                        false
                                                                    }
                                                                    onClick={() =>
                                                                        updateAnswer(
                                                                            elem.id,
                                                                            option?.label,
                                                                            elem.component_id
                                                                        )
                                                                    }
                                                                    checked={elem?.answer?.includes(option?.value)}
                                                                    className={elem?.error ? 'red' : ''}
                                                                />
                                                                {option?.label}
                                                            </div>
                                                        );
                                                    })}
                                                    {errorFields?.includes(elemFieldKey) && (
                                                        <div className="error-text">This field is required</div>
                                                    )}
                                                </div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'explanation-banner') {
                                        return (
                                            <div className="note">
                                                <img src={i} />
                                                {elem?.description}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'file-upload') {
                                        return (
                                            <div className={`Field ${isMatch ? 'edited' : ''}`}>
                                                {elem.component_name && (
                                                    <div className="Field__component">{elem.component_name}</div>
                                                )}
                                                <div className="Field__title">
                                                    <div>
                                                        {elem.label}
                                                        {isRequired && <span className="red">*</span>}
                                                    </div>
                                                    <></>
                                                </div>
                                                <div className="Field__description">{elem.description}</div>
                                                <div className="Field__filepicker">
                                                    <FilePicker
                                                        disabled={false}
                                                        fileList={[elem.answer].filter(Boolean)}
                                                        onChange={(file: any) => {
                                                            if (file?.[0]?.uid) {
                                                                uploadFileHandler(
                                                                    file,
                                                                    saveData?.id,
                                                                    elem.id,
                                                                    elem.component_id
                                                                );
                                                            } else {
                                                                if (!file?.length) {
                                                                    deleteFile(elem.id, elem.component_id);
                                                                }
                                                            }
                                                        }}
                                                        error={
                                                            errorFields?.includes(elemFieldKey)
                                                                ? 'This field is required'
                                                                : ''
                                                        }
                                                        setError={() => {}}
                                                        specialFormats={elem.settings
                                                            ?.find((s: any) => s.type === 'file_upload_formats')
                                                            ?.options?.filter((option: any) => option.selected)
                                                            .flatMap((format: any) =>
                                                                format.label.split(',').map((s: string) => s.trim())
                                                            )}
                                                    />

                                                    {errorFiles?.find((err) => err.key === elemFieldKey)?.error && (
                                                        <div className="error-text">
                                                            {errorFiles?.find((err) => err.key === elemFieldKey)?.error}
                                                        </div>
                                                    )}
                                                </div>
                                                {isMatch && (
                                                    <div className="edited-text">
                                                        <Edit fill="#E3A11F" />
                                                        Edited by Tender Expert
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'group_header') {
                                        return (
                                            <div className="Field">
                                                <div className="Field__slider-title">{elem.label}</div>
                                                <div className="Field__description">{elem.description}</div>
                                            </div>
                                        );
                                    }
                                    if (elem.type === 'slider') {
                                        return (
                                            <div className="expertise">
                                                <label className="expertise__label">
                                                    {elem.label}
                                                    {isRequired && <span className="red">*</span>}
                                                </label>
                                                <div
                                                    className={`expertise__slider ${
                                                        errorFields.includes(elemFieldKey) ? 'error' : ''
                                                    }`}
                                                >
                                                    {elem.options.map((option: any, index: number) => {
                                                        const isLast = index === elem.options.length - 1;

                                                        const selected = elem.answer;

                                                        const hasSelectedAfter = elem.options
                                                            .slice(index + 1)
                                                            .some((opt: any) => opt.value === selected);

                                                        return (
                                                            <React.Fragment key={option.value}>
                                                                <button
                                                                    className={`expertise__dot ${
                                                                        false
                                                                            ? 'disable'
                                                                            : ''
                                                                    } ${hasSelectedAfter ? 'done' : ''} ${
                                                                        elem.answer === option.value ? option.label : ''
                                                                    } `}
                                                                    onClick={() => {
                                                                        if (false) return;

                                                                        updateAnswer(
                                                                            elem.id,
                                                                            option.value,
                                                                            elem.component_id
                                                                        );
                                                                    }}
                                                                >
                                                                    <span>{option.label}</span>
                                                                </button>

                                                                {!isLast && (
                                                                    <div
                                                                        className={`expertise__line ${
                                                                            hasSelectedAfter ? 'line--active' : ''
                                                                        }`}
                                                                    ></div>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </div>
                                                {errorFields.includes(elemFieldKey) && (
                                                    <div className="error-text" style={{ margin: '0 0 10px 0' }}>
                                                        This is a required field
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                }
                                return null;
                            })}
                        </div>
                    </div>
                    <div className="questionary_modal__footer">
                        {steps?.findIndex((item: any) => item.is_active) > 0 && (
                            <Button
                                className="tasks__next-btn"
                                style={{ width: '140px', margin: '0 auto 0 0' }}
                                onClick={() => {
                                    const items = [...steps];
                                    const activeIndex = items.findIndex((item) => item.is_active);
                                    const prevId = activeIndex > 0 ? items[activeIndex - 1].key : null;
                                    if (prevId) {
                                        getFormData(prevId);
                                    }
                                }}
                            >
                                <ArrowDown style={{ transform: 'rotate(90deg)' }} /> Previous
                            </Button>
                        )}
                        <Button
                            className="fields__btn"
                            onClick={handleDiscardAndExit}
                            disabled={isDiscardingPreview}
                        >
                            {isDiscardingPreview ? <Loader color="#001965" /> : 'Discard and Exit'}
                        </Button>
                        <Button className="fields__btn" onClick={() => saveForm('save_and_exit')}>
                            {isLoadingSave && loadingType === 'save_and_exit' ? (
                                <Loader color="#001965" />
                            ) : (
                                'Save and Exit'
                            )}
                        </Button>
                        <Button
                            className="fields__btn"
                            type="primary"
                            onClick={() => saveForm('next')}
                            disabled={
                                isLoadingSave ||
                                (saveData &&
                                    isServiceComponents &&
                                    (!saveData?.fields?.[0]?.answer || saveData?.fields?.[0]?.answer?.length < 1))
                            }
                        >
                            {isLoadingSave && loadingType === 'next' ? (
                                <Loader />
                            ) : (
                                <div className="questionary_modal__btn">
                                    Next <ArrowDown style={{ transform: 'rotate(-90deg)' }} fill="white" />
                                </div>
                            )}
                        </Button>
                    </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};
